import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { configs } from 'configs'
import { ROLE } from 'constant'
import { showRefreshTimeout } from 'libs/refreshTimeoutOverlay'
import { storageServices } from 'services'

const REFRESH_REQUEST_TIMEOUT_MS = 20_000

interface CreateAxiosConfig {
  withAuthToken: boolean
  withFile?: boolean
}

const defaultConfig: CreateAxiosConfig = {
  withAuthToken: true,
  withFile: false,
}

/**
 * Creates an axios instance with baseURL and optional auth/refresh interceptors.
 */
export function createAxios(
  config?: Partial<CreateAxiosConfig>,
): AxiosInstance {
  const merged: CreateAxiosConfig = { ...defaultConfig, ...config }

  const client = axios.create({
    baseURL: `${configs.apiURL}/api`,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': merged.withFile
        ? 'application/zip'
        : 'application/json;charset=utf-8',
    },
  })

  if (merged.withAuthToken) {
    client.interceptors.request.use(
      (req: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        const token = storageServices.getAccessToken()
        if (token) {
          req.headers.Authorization = `Bearer ${token}`
        }
        return req
      },
    )

    client.interceptors.response.use(
      res => res,
      async error => {
        if (!navigator.onLine) {
          return Promise.reject({
            ...error,
            message: 'ネットワークの問題が発生しました。',
          })
        }
        const refreshToken = storageServices.getRefreshToken()
        if (!refreshToken) return Promise.reject(error)

        const request = error.config
        const status = error.response?.status
        const shouldRetry =
          merged.withAuthToken &&
          (status === 401 || status === 403) &&
          !request._retry

        if (!shouldRetry) return Promise.reject(error)

        request._retry = true
        const refreshPath =
          storageServices.getRoleUser() === ROLE.ADMIN
            ? '/v1/admin/oauth/refresh'
            : '/v1/advisor/oauth/refresh'

        try {
          const { data } = await client.post(
            refreshPath,
            {
              refresh_token: refreshToken,
            },
            { timeout: REFRESH_REQUEST_TIMEOUT_MS },
          )
          const accessToken = data?.data?.access_token
          storageServices.setAccessToken(accessToken)
          client.defaults.headers.common[
            'Authorization'
          ] = `Bearer ${accessToken}`
          return client(request)
        } catch {
          showRefreshTimeout()
          return Promise.reject(error)
        }
      },
    )
  }

  return client
}
